import { smooth_fft } from "./fft.utils";

import { FormattedChartData } from "./types";

export type ResistanceViewMode = "raw" | "firstDerivative" | "secondDerivative";

const viewSuffix: Record<Exclude<ResistanceViewMode, "raw">, string> = {
  firstDerivative: "1st Derivative",
  secondDerivative: "2nd Derivative",
};

const LEADING_POINTS_TO_REMOVE = 5;
const FFT_WINDOW = 50;

const toLog10 = (value: number): number => {
  if (!Number.isFinite(value) || value <= 0) {
    return Number.NaN;
  }

  return Math.log10(value);
};

export const applySmoothing = (data: number[]): number[] => {
  if (data.length < 3) {
    return data;
  }

  return smooth_fft(data, FFT_WINDOW, 1);
};

const prepareDataForDerivatives = (
  data: FormattedChartData["data"],
  options: { useLog10: boolean } = { useLog10: true }
): FormattedChartData["data"] => {
  if (data.length === 0) {
    return [];
  }

  const processedData = data.map((point) => ({
    x: point.x,
    y: options.useLog10 ? toLog10(point.y) : point.y,
  }));

  const mask = processedData.map((point) => Number.isFinite(point.y));

  if (!mask.some(Boolean)) {
    return [];
  }

  const forwardFill: number[] = new Array(processedData.length).fill(
    Number.NaN
  );
  let lastFinite: number | null = null;
  processedData.forEach((point, index) => {
    if (Number.isFinite(point.y)) {
      lastFinite = point.y;
      forwardFill[index] = point.y;
    } else if (lastFinite !== null) {
      forwardFill[index] = lastFinite;
    }
  });

  const backwardFill: number[] = new Array(processedData.length).fill(
    Number.NaN
  );
  let nextFinite: number | null = null;
  for (let index = processedData.length - 1; index >= 0; index -= 1) {
    const point = processedData[index];
    if (Number.isFinite(point.y)) {
      nextFinite = point.y;
      backwardFill[index] = point.y;
    } else if (nextFinite !== null) {
      backwardFill[index] = nextFinite;
    }
  }

  const sanitized = processedData.map((point, index) => {
    if (Number.isFinite(point.y)) {
      return point.y as number;
    }

    if (Number.isFinite(forwardFill[index])) {
      return forwardFill[index];
    }

    if (Number.isFinite(backwardFill[index])) {
      return backwardFill[index];
    }

    return 0;
  });

  const smoothedValues = applySmoothing(sanitized);

  const processed = processedData.map((point, index) => ({
    x: point.x,
    y: mask[index] ? smoothedValues[index] : Number.NaN,
  }));

  if (processed.length <= LEADING_POINTS_TO_REMOVE) {
    return [];
  }

  return processed.slice(LEADING_POINTS_TO_REMOVE);
};

const computeDerivativePoints = (
  data: FormattedChartData["data"]
): FormattedChartData["data"] => {
  if (data.length < 2) {
    return [];
  }

  const derivative: FormattedChartData["data"] = [];

  for (let index = 1; index < data.length; index += 1) {
    const current = data[index];
    const previous = data[index - 1];
    const deltaX = current.x - previous.x;

    if (!Number.isFinite(deltaX) || deltaX === 0) {
      derivative.push({ x: current.x, y: Number.NaN });
      continue;
    }

    if (!Number.isFinite(current.y) || !Number.isFinite(previous.y)) {
      derivative.push({ x: current.x, y: Number.NaN });
      continue;
    }

    derivative.push({
      x: current.x,
      y: (current.y - previous.y) / deltaX,
    });
  }

  return derivative;
};

const applyDerivativeOrder = (
  data: FormattedChartData["data"],
  order: number
): FormattedChartData["data"] => {
  let result = data;

  for (let iteration = 0; iteration < order; iteration += 1) {
    result = computeDerivativePoints(result);
    if (result.length === 0) {
      break;
    }
  }

  return result;
};

export const transformLinesForResistanceView = (
  lines: FormattedChartData[],
  view: ResistanceViewMode,
  isSmoothed: boolean
): FormattedChartData[] => {
  return lines.map((line) => {
    // Always prepare data (log10 + smooth) for derivatives or if smoothing is requested
    // But if view is raw and NOT smoothed, we want the original raw data (but maybe we need to handle log scale in the chart component)
    // Wait, if view is raw and NOT smoothed, we return the original line data.
    // If view is raw and smoothed, we return the prepared data (which is smoothed log10).

    if (view === "raw" && !isSmoothed) {
      return line;
    }

    const prepared = prepareDataForDerivatives(line.data, { useLog10: true });

    if (view === "raw" && isSmoothed) {
      return {
        ...line,
        id: `${line.id} (Smoothed log10)`,
        data: prepared,
      };
    }

    const suffix = viewSuffix[view as Exclude<ResistanceViewMode, "raw">];
    const order = view === "firstDerivative" ? 1 : 2;
    let derivedData = applyDerivativeOrder(prepared, order);

    if (isSmoothed) {
      // Apply additional smoothing to the derivative result
      // We need to extract Y values, smooth them, and put them back
      // But we need to handle NaNs carefully like in prepareDataForDerivatives
      // For simplicity, let's just smooth the valid values for now or reuse a simplified smoothing helper if possible
      // Actually, let's just use applySmoothing on the y values where possible.
      // But applySmoothing expects a continuous array of numbers without NaNs for best results.
      // Let's do a simple pass: extract valid Ys, smooth, put back.

      const yValues = derivedData.map((d) => d.y);
      // Simple fill for NaNs to allow smoothing
      const filledY = yValues.map((y) => (Number.isFinite(y) ? y : 0)); // This might be too naive
      // Better: just smooth what we have, assuming derivatives are mostly continuous
      // If we have gaps, smoothing might look weird at edges.
      // Let's use the same logic as prepareDataForDerivatives but without log10
      // Actually, let's just reuse prepareDataForDerivatives with useLog10=false!
      derivedData = prepareDataForDerivatives(derivedData, { useLog10: false });
    }

    return {
      ...line,
      id: `${line.id} (${suffix}${isSmoothed ? " Smoothed" : ""})`,
      data: derivedData,
    };
  });
};

export const transformLinesForCapacitanceView = (
  lines: FormattedChartData[],
  view: ResistanceViewMode,
  isSmoothed: boolean
): FormattedChartData[] => {
  return lines.map((line) => {
    if (view === "raw" && !isSmoothed) {
      return line;
    }

    // For capacitance, we don't use log10
    const prepared = prepareDataForDerivatives(line.data, { useLog10: false });

    if (view === "raw" && isSmoothed) {
      return {
        ...line,
        id: `${line.id} (Smoothed)`,
        data: prepared,
      };
    }

    const suffix = viewSuffix[view as Exclude<ResistanceViewMode, "raw">];
    const order = view === "firstDerivative" ? 1 : 2;
    let derivedData = applyDerivativeOrder(prepared, order);

    if (isSmoothed) {
      derivedData = prepareDataForDerivatives(derivedData, { useLog10: false });
    }

    return {
      ...line,
      id: `${line.id} (${suffix}${isSmoothed ? " Smoothed" : ""})`,
      data: derivedData,
    };
  });
};
